import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot antispam felt
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }

      setStatus({ submitting: false, success: true, error: null });
      setFormData({
        name: '',
        phone: '',
        email: '',
        description: '',
        website_url: '',
      });
    } catch (err) {
      setStatus({ submitting: false, success: false, error: err.message });
    }
  };

  return (
    <div className="bg-white p-8 sm:p-10 rounded-3xl shadow-md border border-cream-200">
      <h3 className="text-2xl font-serif font-bold text-forest-950 mb-6">Send os en besked</h3>

      {status.success ? (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-2xl flex flex-col items-center text-center gap-3">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-xl">
            <i className="fa-solid fa-circle-check" />
          </div>
          <div>
            <h4 className="font-semibold text-lg">Mange tak for din besked!</h4>
            <p className="text-sm mt-1">Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Honeypot felt - ABSOLUT SKJULT for mennesker */}
          <div className="sr-only absolute pointer-events-none" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this out if you are a human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Navn */}
          <div className="flex flex-col gap-2">
            <label htmlFor="name" className="text-sm font-semibold text-charcoal-800">
              Navn <span className="text-dahlia-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-cream-300 focus:border-dahlia-500 focus:ring-2 focus:ring-dahlia-200 outline-none transition-all text-charcoal-900 bg-cream-50/30"
              placeholder="Dit fulde navn"
            />
          </div>

          {/* Telefonnummer & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2">
              <label htmlFor="phone" className="text-sm font-semibold text-charcoal-800">
                Telefonnummer <span className="text-dahlia-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-cream-300 focus:border-dahlia-500 focus:ring-2 focus:ring-dahlia-200 outline-none transition-all text-charcoal-900 bg-cream-50/30"
                placeholder="Dit telefonnummer"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="text-sm font-semibold text-charcoal-800">
                E-mail <span className="text-dahlia-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-cream-300 focus:border-dahlia-500 focus:ring-2 focus:ring-dahlia-200 outline-none transition-all text-charcoal-900 bg-cream-50/30"
                placeholder="Din e-mailadresse"
              />
            </div>
          </div>

          {/* Beskrivelse */}
          <div className="flex flex-col gap-2">
            <label htmlFor="description" className="text-sm font-semibold text-charcoal-800">
              Beskrivelse / Spørgsmål <span className="text-dahlia-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="5"
              value={formData.description}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-cream-300 focus:border-dahlia-500 focus:ring-2 focus:ring-dahlia-200 outline-none transition-all text-charcoal-900 bg-cream-50/30 resize-none"
              placeholder="Skriv dit spørgsmål eller besked her..."
            />
          </div>

          {status.error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-xl text-sm flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation text-base" />
              <span>{status.error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status.submitting}
            className="w-full bg-black hover:bg-neutral-900 disabled:bg-neutral-400 text-white font-semibold py-4 rounded-full shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-spinner animate-spin" />
                Sender besked...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" />
                Send besked
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
